import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Loader2 } from "lucide-react";
import { http, apiError } from "@/lib/shop";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminProducts } from "@/components/admin/AdminProducts";
import { AdminRepairs } from "@/components/admin/AdminRepairs";
import { PartsInventory } from "@/components/admin/parts/PartsInventory";
import { MovementHistory } from "@/components/admin/parts/MovementHistory";
import { StaffManager } from "@/components/admin/StaffManager";

export default function Admin() {
  const [user, setUser] = useState(null);

  const check = useCallback(async () => {
    try {
      setUser((await http.get("/auth/me")).data);
    } catch {
      try {
        await http.post("/auth/refresh");
        setUser((await http.get("/auth/me")).data);
      } catch {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const logout = async () => { await http.post("/auth/logout"); setUser(false); };

  if (user === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand-600" /></div>;
  if (!user) return <Login onDone={setUser} />;
  const owner = user.role === "admin";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="font-display text-xl font-black">FONZ <span className="text-brand">FIX</span>4<span className="text-brand">U</span> <span className="text-sm font-semibold text-slate-400 ml-2" data-testid="admin-role-label">{owner ? "Shop admin" : `Staff · ${user.name || user.email}`}</span></a>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold hover:text-brand-400" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-8">
        <Tabs defaultValue={owner ? "products" : "parts"}>
          <TabsList className="flex-wrap h-auto">
            {owner && <TabsTrigger value="products" data-testid="admin-tab-products">Phones & products</TabsTrigger>}
            <TabsTrigger value="repairs" data-testid="admin-tab-repairs">Repair requests</TabsTrigger>
            <TabsTrigger value="parts" data-testid="admin-tab-parts">Parts inventory</TabsTrigger>
            <TabsTrigger value="history" data-testid="admin-tab-history">Stock history</TabsTrigger>
            {owner && <TabsTrigger value="staff" data-testid="admin-tab-staff">Staff</TabsTrigger>}
          </TabsList>
          {owner && <TabsContent value="products"><AdminProducts /></TabsContent>}
          <TabsContent value="repairs"><AdminRepairs owner={owner} /></TabsContent>
          <TabsContent value="parts"><PartsInventory owner={owner} /></TabsContent>
          <TabsContent value="history"><MovementHistory owner={owner} /></TabsContent>
          {owner && <TabsContent value="staff"><StaffManager /></TabsContent>}
        </Tabs>
      </main>
    </div>
  );
}

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      onDone((await http.post("/auth/login", { email, password })).data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 p-8 space-y-4" data-testid="admin-login-form">
        <p className="font-display text-2xl font-black">FONZ <span className="text-brand-600">FIX4U</span></p>
        <p className="text-sm text-slate-600">Shop login — manage phones, prices and repair requests.</p>
        <div className="grid gap-1.5"><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" /></div>
        <div className="grid gap-1.5"><Label>Password</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" /></div>
        <button disabled={busy} className="w-full rounded-full bg-brand-600 py-3 font-bold text-white hover:bg-brand-700 disabled:opacity-60 transition-colors" data-testid="admin-login-btn">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
    </div>
  );
};
